// Datos estructurales del landing: ids, iconos, agrupaciones por plan.
// Los textos visibles (label, name, desc) viven en src/locales/landing/*.json
// y se leen vía useTranslation(). Aquí sólo lo que NO se internacionaliza:
// referencias a iconos de lucide-react, tier ids y precios numéricos.

import {
  Baby,
  BarChart3,
  CalendarDays,
  Clock,
  Coffee,
  CreditCard,
  QrCode,
  Smartphone,
  Users,
  WifiOff,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

// ─── Hero / Features (9 cards) ───────────────────────────────────────────
// Cada feature lee `landing.features.<key>.label` y `landing.features.<key>.desc`.
/** Decoración 3D asociada a una fila concreta. `kind` mapea a la clase
 *  CSS `.playium-modules__row-deco--<kind>` que define posición, tamaño
 *  y animación. */
export interface FeatureDeco {
  src: string;
  kind:
    | "gear-right"
    | "gear-right-pink"
    | "arch-left-lavender"
    | "arch-right-mint"
    | "star-top-left"
    | "star-mid-right"
    | "star-bot-left"
    | "balls-left"
    | "balls-right";
}

export interface FeatureMeta {
  icon: LucideIcon;
  /** Sub-clave bajo `landing.features` en los JSON (ej. "playground"). */
  key: string;
  /** PNG 3D del kit (icono coloreado con bg propio). Si está presente la
   *  galería lo usa en vez del Lucide + chip de fallback. */
  icon3d?: string;
  /** Captura final compuesta del módulo (con 3D ya integrados), SIN
   *  extensión: `Screenshot` pide `<shot>[-640|-960].{avif,webp}` (LDT-717).
   *  Si está presente sobreescribe el slug-based default de ModulePicture. */
  shot?: string;
  /** 3D decorativos que asoman por las gutters (fuera del container 1440)
   *  asociados a esta fila. */
  decos?: FeatureDeco[];
}

export const FEATURES: FeatureMeta[] = [
  {
    icon: Baby,
    key: "playground",
    icon3d: "/icons/ICONO%20PARQUE%20INFANTIL.webp",
    shot: "/screenshots/3_1_home_parque_infantil",
    decos: [{ src: "/icons/GEAR_CUT_3.webp", kind: "gear-right" }],
  },
  {
    icon: Coffee,
    key: "cafe",
    icon3d: "/icons/ICONO%20CAFETERIA.webp",
    shot: "/screenshots/3_2_home_cafeteria",
  },
  {
    icon: Clock,
    key: "bonos",
    icon3d: "/icons/ICONO%20BONOS.webp",
    shot: "/screenshots/3_3_home_bonos",
    decos: [
      { src: "/icons/ARCH_SYSTEM_2.webp", kind: "arch-left-lavender" },
      { src: "/icons/ICONO%20ESTRELLA.webp", kind: "star-bot-left" },
      { src: "/icons/ICONO_PILOTES.webp", kind: "balls-right" },
    ],
  },
  {
    icon: CalendarDays,
    key: "events",
    icon3d: "/icons/ICONO%20EVENTOS.webp",
    shot: "/screenshots/3_4_home_eventos",
  },
  {
    icon: Users,
    key: "members",
    icon3d: "/icons/ICONO%20MEMBRESIAS.webp",
    shot: "/screenshots/3_5_home_membresias",
    decos: [{ src: "/icons/ICONO%20ESTRELLA.webp", kind: "star-mid-right" }],
  },
  {
    icon: BarChart3,
    key: "dashboard",
    icon3d: "/icons/ICONO%20DASHBOARD.webp",
    shot: "/screenshots/3_6_home_dashboard",
  },
  {
    icon: QrCode,
    key: "qr",
    icon3d: "/icons/ICONO%20QR.webp",
    shot: "/screenshots/3_7_home_qr_easypass",
  },
  {
    icon: Smartphone,
    key: "app",
    icon3d: "/icons/ICONO%20FAMILIAS.webp",
    shot: "/screenshots/3_8_home_familias",
    decos: [{ src: "/icons/ICONO%20ESTRELLA.webp", kind: "star-top-left" }],
  },
  {
    icon: CreditCard,
    key: "payments",
    icon3d: "/icons/ICONO%20PAGOS.webp",
    shot: "/screenshots/3_9_home_pagos_online",
    decos: [{ src: "/icons/GEAR_CUT_2.webp", kind: "gear-right-pink" }],
  },
  // §11.1 del audit + endorsement de founder-decisions §4: la operación offline
  // (Dexie + PWA + sync) es un diferenciador duro que el TPV genérico de
  // hostelería no ofrece. Va como décima feature visible, no como FAQ.
  {
    icon: WifiOff,
    key: "offline",
    icon3d: "/icons/ICONO%20NO%20WIFI.webp",
    shot: "/screenshots/3_10_home_sin_internet",
    decos: [
      { src: "/icons/ARCH_SYSTEM_1.webp", kind: "arch-right-mint" },
      { src: "/icons/ICONO_PILOTES.webp", kind: "balls-left" },
    ],
  },
];

// ─── Pricing tiers ───────────────────────────────────────────────────────
// `key` mapea a `landing.pricing.<key>` (objeto con name, price, desc, cta,
// features[]). `priceNumeric` se conserva por si en el futuro hace falta
// para algun calculo (configurador, comparativa); `chains` no tiene precio
// numerico al ser "Personalizado". `highlighted` marca el tier destacado
// visualmente (Growth es el "Mas popular" / launch offer 99€ jun-ago).
// Orden vinculante visual: Starter -> Events -> Growth -> Chains.
export type PricingTierKey = "starter" | "events" | "growth" | "chains";

export interface PricingTierMeta {
  key: PricingTierKey;
  /** Precio base en € (0 = cotizacion a medida, como Chains). */
  priceNumeric: number;
  /** Visualmente resaltado (Growth). */
  highlighted: boolean;
}

export const PRICING_TIERS: PricingTierMeta[] = [
  { key: "starter", priceNumeric: 79, highlighted: false },
  { key: "events", priceNumeric: 99, highlighted: false },
  { key: "growth", priceNumeric: 149, highlighted: true },
  { key: "chains", priceNumeric: 0, highlighted: false },
];

// ─── Pricing packs (landing hub) ─────────────────────────────────────────
// Modelo self-serve de 2 packs (Starter / Growth) que sustituye al antiguo
// selector por tipo de parque. Starter es el "más vendido" (highlighted).
// Growth incluye todo Starter + las features GREAT y permite añadir los
// módulos opcionales (ver OPTIONAL_MODULES: el precio no se repite aquí para
// que no se quede atrás). Textos en i18n bajo `pricing.packs.<key>` y
// `pricing.modules.<key>`.
export type PackKey = "starter" | "growth";
export type BillingCycle = "monthly" | "annual";

// Anual = se pagan (12 − ANNUAL_FREE_MONTHS) cuotas: 1 cuota de descuento.
export const ANNUAL_FREE_MONTHS = 1;

// Meses que se cobran en el ciclo anual (12 − cuotas gratis). Anual = 11.
// Fuente única para el cálculo del precio anual (pricing cards + modal de
// captación) — no re-derivar `12 - ANNUAL_FREE_MONTHS` en cada consumidor.
export const PAID_MONTHS = 12 - ANNUAL_FREE_MONTHS;

/** Último día de la oferta, incluido (ISO, hora local).
 *
 *  LA OFERTA DE VERANO TERMINÓ (LDT-556, 2026-09-16) y los packs volvieron a
 *  su precio estándar. La maquinaria se queda DORMIDA, no borrada: esta
 *  constante, `summerPriceDaysLeft()`, `SummerOfferBanner`, el copy
 *  `pricing.offer.*` y el precio tachado de las tarjetas siguen en su sitio,
 *  simplemente sin oferta que anunciar.
 *
 *  PARA REACTIVAR UNA OFERTA hacen falta exactamente dos cosas:
 *    1. una fecha nueva aquí, y
 *    2. un `standardMonthlyPrice` en los packs que estén de oferta (el precio
 *       tachado), bajando su `monthlyPrice`.
 *  Nada más: el aviso, el tachado y el % de descuento se encienden solos.
 *
 *  Y AL REVÉS, cuando la oferta caduque: la fecha apaga el aviso sola, pero
 *  los precios NO se actualizan solos. Hay que subir `monthlyPrice` aquí, en
 *  `_shared/billing/catalog.ts` y en `BillingPanel.tsx` —los tres a la vez,
 *  que es lo que vigila billing-catalog-consistency.test.ts— y además subir
 *  `CATALOG_VERSION` para que se creen PRODUCTOS NUEVOS en la pasarela: en
 *  Dodo el modelo de precio es inmutable y cambiar el importe de un producto
 *  existente no se puede. Las suscripciones ya vivas conservan su precio. */
export const SUMMER_PRICE_ENDS = "2026-09-15";

/** Días que quedan de precio de oferta, o 0 si ya terminó.
 *
 *  Se calcula en cada render a propósito: si alguien deja la pestaña abierta
 *  toda la noche, al volver ve el número bueno y no uno congelado. */
export function summerPriceDaysLeft(now: Date = new Date()): number {
  const end = new Date(`${SUMMER_PRICE_ENDS}T23:59:59`);
  const ms = end.getTime() - now.getTime();
  return ms <= 0 ? 0 : Math.ceil(ms / 86_400_000);
}

/** `priceValidUntil` para el JSON-LD de /pricing, o `undefined` si no hay
 *  oferta viva.
 *
 *  Un `priceValidUntil` con fecha PASADA es decirle a Google que el precio
 *  que acaba de leer ya caducó; el campo solo tiene sentido mientras el
 *  precio anunciado sea temporal, así que fuera de la oferta se OMITE. */
export function offerPriceValidUntil(now: Date = new Date()): string | undefined {
  return summerPriceDaysLeft(now) > 0 ? SUMMER_PRICE_ENDS : undefined;
}

export interface PackMeta {
  key: PackKey;
  /** Precio mensual en € (base para mensual y para el cálculo del anual). */
  monthlyPrice: number;
  /** Precio TACHADO de la tarjeta: el precio estándar del que cuelga una
   *  oferta temporal. **Opcional, y hoy no lo declara ningún pack** — no hay
   *  oferta activa desde el 2026-09-16 (LDT-556), así que `monthlyPrice` YA
   *  es el precio estándar y no hay nada que tachar.
   *
   *  Declararlo es lo que enciende el tachado, el badge de descuento y el %
   *  (que se calcula solo a partir de los dos importes). Es un precio REAL,
   *  no un adorno: hay parques activos pagándolo. Esa es la condición para
   *  poder tacharlo — un precio de referencia que nunca se ha aplicado es
   *  publicidad engañosa, y quien lo ponga aquí debería poder señalar
   *  clientes que lo pagan.
   *
   *  Lo vigila billing-catalog-consistency.test.ts: si está, tiene que ser
   *  MAYOR que `monthlyPrice`, o la tarjeta anunciaría un descuento al revés. */
  standardMonthlyPrice?: number;
  /** Icono grande del header de la card. */
  headerIcon: string;
  /** Iconos 1:1 con `pricing.packs.<key>.features` de i18n (el orden importa). */
  featureIcons: string[];
  /** Resaltado visualmente + badge "Más vendido". */
  highlighted: boolean;
  /** Módulos opcionales que ofrece este pack (cada pack tiene los suyos). */
  availableModules: ModuleKey[];
}

export const PACKS: PackMeta[] = [
  {
    key: "starter",
    // Precio estándar desde el 2026-09-16 (fin de la oferta de verano, que
    // lo dejaba en 35 €). Sin `standardMonthlyPrice`: no hay nada que tachar.
    // Ver el aviso de SUMMER_PRICE_ENDS sobre qué más hay que tocar al
    // cambiar este número.
    monthlyPrice: 49.95,
    headerIcon: "/images/rates/park/BOLAS-STARTER.webp",
    featureIcons: [
      "/images/rates/park/STARTER-2-CRONOMETRO.webp",
      "/images/rates/park/PRO-1-BONOS.webp",
      "/images/rates/park/PRO-6-CUSTOMIZER.webp",
      "/images/rates/park/STARTER-1-TPV.webp",
      "/images/rates/park/STARTER-4-CATALOGO.webp",
      "/images/rates/events/STARTER-3-CALENDARIO_PUBLICO.webp",
      "/images/rates/mix/STANDARD-5-DASHBOARD.webp",
    ],
    highlighted: true,
    // En Growth el bot de WhatsApp viene incluido; en Starter es opcional.
    availableModules: ["whatsapp"],
  },
  {
    key: "growth",
    // Precio estándar desde el 2026-09-16 (la oferta lo dejaba en 70 €).
    monthlyPrice: 89.95,
    headerIcon: "/images/rates/mix/MIXTO-PRO.webp",
    featureIcons: [
      "/images/rates/events/PRO-3-BRANDING.webp",
      "/images/rates/mix/PRO-3-QREASY.webp",
      "/images/rates/mix/PRO-5-APPFAMILIAS.webp",
      "/images/rates/events/STARTER-2-PAGOS.webp",
      "/images/rates/mix/PRO-6-WHATSAPP.webp",
      "/images/rates/mix/PRO-4-DASHBOARD.webp",
    ],
    highlighted: false,
    availableModules: ["matriculas", "membresias"],
  },
];

export type ModuleKey = "matriculas" | "membresias" | "whatsapp";

export interface ModuleMeta {
  key: ModuleKey;
  /** Precio mensual en € del módulo opcional. */
  monthlyPrice: number;
  icon: string;
}

// Precios estándar desde el 2026-09-16 (la oferta de verano los dejaba en
// 5 / 5 / 15 €). Tienen que coincidir con `MODULE_MONTHLY_EUR` del catálogo
// del servidor y con `MODULE_MONTHLY` del panel.
export const OPTIONAL_MODULES: ModuleMeta[] = [
  { key: "matriculas", monthlyPrice: 10, icon: "/images/rates/mix/STANDARD-4-PAGOS%20MIXTOS.webp" },
  { key: "membresias", monthlyPrice: 10, icon: "/images/rates/mix/PRO-2-MEMBRESIAS.webp" },
  { key: "whatsapp", monthlyPrice: 20, icon: "/images/rates/mix/PRO-6-WHATSAPP.webp" },
];
